import { Divider, Heading, RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'
import { useState } from 'react'
import { HttpVerb } from '../../../../../types/api-types'
import { FormDelegate, PageNavigation } from '../../../../../types/routing-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import NotesWrapperEdit from '../../../../components/well-forms/notes/notes-wrapper-edit'
import EditWellHeadSecurityConstruction from '../../../../components/well-forms/wellhead-security/well-head-security-edit-construction'
import EditWellHeadSecurityQuality from '../../../../components/well-forms/wellhead-security/well-head-security-edit-quality'
import EditWellHeadSecuritySurroundingArea from '../../../../components/well-forms/wellhead-security/well-head-security-edit-surrounding-area'
import EditRecordButtons from '../../../../components/wells/edit-record-buttons'
import { useWellState } from '../../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import useUser from '../../../../hooks/use-user'
import { useWellId } from '../../../../hooks/use-well-id'
import { isDriller } from '../../../../services/user-utils'

export function EditWellWellheadSecurity({ setIsDirty }: FormDelegate) {
  const {
    wellState: { isDraft },
  } = useWellState()
  const { user } = useUser()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Construction`)
  const wellId = useWellId()

  const urls: Omit<PageNavigation, 'saveAndReturnRoute'> = {
    requestUrl: `wells/${wellId}/wellhead-security/edit`,
    method: HttpVerb.PUT,
    saveAndContinueRoute: (id: string | number) => {
      if (isDriller(user)) {
        return `/wells/${id}/edit/drilling-tests`
      }
      return `/wells/${id}/edit/drinking-water-supply`
    },
    cancelButtonLabel: isDraft ? 'Cancel' : 'Return to view',
    cancelRoute: isDraft ? '/dashboard' : `/wells/${wellId}/view/wellhead-security`,
  }

  const stateQueryResult = useGetWellData(urls.requestUrl)
  const saveMutationResult = useSaveWellData(urls.requestUrl, urls.method)

  const {
    fields,
    error,
    isLoading,
    isSaveError,
    isSaved,
    setValue,
    handleSave,
    addField,
    editField,
    removeField,
    validationErrors,
  } = useEditForm({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  return (
    <>
      <RecordContent>
        <Heading isSkeleton={isLoading} level={2}>
          Wellhead security
        </Heading>

        <EditWellHeadSecurityQuality
          fields={fields}
          isLoading={isLoading}
          setValue={setValue}
          validationErrors={validationErrors ?? []}
        />

        <Divider />

        <EditWellHeadSecurityConstruction
          fields={fields}
          isLoading={isLoading}
          setValue={setValue}
          validationErrors={validationErrors ?? []}
        />

        <Divider />

        <EditWellHeadSecuritySurroundingArea
          fields={fields}
          isLoading={isLoading}
          setValue={setValue}
          validationErrors={validationErrors ?? []}
        />
      </RecordContent>

      <RecordFooter
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
        areActionsFixed={true}
      >
        <EditRecordButtons handleSave={handleSave} urls={urls} />
      </RecordFooter>
    </>
  )
}

export default function EditWellWellheadSecurityWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditWellWellheadSecurity setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
